import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Activity, RefreshCw, Loader2, Factory, Wrench, Gauge,
    WifiOff, AlertOctagon, Siren, ChevronRight, AlertTriangle,
    CalendarClock, Zap, TrendingUp, TrendingDown, MapPin,
    ExternalLink, Inbox, Lock, ShieldCheck, ShieldAlert, ShieldX, Package
} from 'lucide-react';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';
const DISPLAY_FONT = { fontFamily: "'Chakra Petch', 'Segoe UI', system-ui, sans-serif" };
const nf = new Intl.NumberFormat('ru-RU');

const plural = (n, one, few, many) => {
    const abs = Math.abs(n) % 100;
    const d = abs % 10;
    if (abs > 10 && abs < 20) return many;
    if (d === 1) return one;
    if (d >= 2 && d <= 4) return few;
    return many;
};

const useCountUp = (target, duration = 900) => {
    const [value, setValue] = useState(0);
    useEffect(() => {
        if (target == null || Number.isNaN(target)) return;
        let raf;
        const start = performance.now();
        const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(target * eased));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);
    return target == null ? null : value;
};

const AnimatedNumber = ({ value, className = '' }) => {
    const v = useCountUp(value);
    return (
        <span className={`tabular-nums ${className}`} style={DISPLAY_FONT}>
            {v == null ? '—' : nf.format(v)}
        </span>
    );
};

const formatAddress = (d) => {
    const parts = [];
    if (d.city) parts.push(d.city);
    if (d.street) parts.push(d.street);
    if (d.houseNumber) parts.push(`д. ${d.houseNumber}`);
    return parts.length ? parts.join(', ') : 'Адрес не указан';
};

// ✅ Статус опломбирования — аналог getPlombStatus() из HousePage.jsx
const getPlombStatus = (node) => {
    if (!node.isOplombirovan || !node.oplombirovanValidUntil) {
        return { status: 'none', label: 'Не опломбирован', days: null };
    }
    const parts = node.oplombirovanValidUntil.split('.');
    if (parts.length !== 3) return { status: 'none', label: 'Нет даты', days: null };
    const [day, month, year] = parts;
    const validUntil = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    validUntil.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((validUntil - today) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { status: 'expired', label: 'Просрочено', days: diffDays };
    if (diffDays <= 10) return { status: 'warning', label: 'Скоро', days: diffDays };
    return { status: 'ok', label: 'Действует', days: diffDays };
};

const PLOMB_META = {
    expired: {
        icon: ShieldX,
        border: 'border-l-red-500',
        badge: 'bg-red-500/15 border-red-500/40 text-red-400',
        iconCls: 'text-red-400',
        bg: 'bg-red-500/5',
    },
    warning: {
        icon: ShieldAlert,
        border: 'border-l-yellow-500',
        badge: 'bg-yellow-500/15 border-yellow-500/40 text-yellow-400',
        iconCls: 'text-yellow-400',
        bg: 'bg-yellow-500/5',
    },
    ok: {
        icon: ShieldCheck,
        border: 'border-l-green-500/70',
        badge: 'bg-green-500/15 border-green-500/40 text-green-400',
        iconCls: 'text-green-400',
        bg: '',
    },
    none: {
        icon: Lock,
        border: 'border-l-slate-600',
        badge: 'bg-slate-600/20 border-slate-600/50 text-slate-400',
        iconCls: 'text-slate-500',
        bg: '',
    },
};

const StatisticsPage = () => {
    const navigate = useNavigate();

    // ===== Основная сводка =====
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    // ===== Аномалии =====
    const [anomalies, setAnomalies] = useState(null);
    const [anomaliesLoading, setAnomaliesLoading] = useState(true);
    const [anomaliesError, setAnomaliesError] = useState(null);
    const [anomaliesFromCache, setAnomaliesFromCache] = useState(false);

    // ===== График опломбирования =====
    const [plombList, setPlombList] = useState(null);
    const [plombLoading, setPlombLoading] = useState(true);
    const [plombError, setPlombError] = useState(null);

    const loadStats = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        try {
            const { data } = await axios.get(`${API_URL}/api/statistics/summary`, { headers });
            setStats({
                totalDevices: data.totalDevices ?? null,
                servicedDevices: data.servicedDevices ?? null,
                lostCount: data.lostCount ?? null,
                criticalLostCount: data.criticalLostCount ?? null,
                devicesWithMultipleAlarms: data.devicesWithMultipleAlarms ?? null,
                alarmsScanDate: data.alarmsScanDate ?? null,
            });
            setLastUpdated(new Date());
        } catch (e) {
            try {
                const { data } = await axios.get(`${API_URL}/api/ns/lost-devices`, { headers });
                const list = data || [];
                setStats({
                    totalDevices: null, servicedDevices: null,
                    lostCount: list.length,
                    criticalLostCount: list.filter(d => (d.daysOffline ?? 0) >= 7).length,
                    devicesWithMultipleAlarms: null, alarmsScanDate: null,
                });
                setLastUpdated(new Date());
            } catch {
                setError('Не удалось загрузить сводные данные');
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    const loadAnomalies = useCallback(async () => {
        setAnomaliesLoading(true);
        setAnomaliesError(null);
        try {
            const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
            const { data } = await axios.get(`${API_URL}/api/statistics/anomalies`, { headers });
            setAnomalies(data.devices || []);
            setAnomaliesFromCache(data.fromCache ?? false);
        } catch {
            setAnomaliesError('Не удалось загрузить данные об аномалиях');
        } finally {
            setAnomaliesLoading(false);
        }
    }, []);

    const loadPlomb = useCallback(async () => {
        setPlombLoading(true);
        setPlombError(null);
        try {
            const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
            const { data } = await axios.get(`${API_URL}/api/statistics/plomb-schedule`, { headers });
            setPlombList(data || []);
        } catch {
            setPlombError('Не удалось загрузить график опломбирования');
        } finally {
            setPlombLoading(false);
        }
    }, []);

    useEffect(() => { loadStats(); }, [loadStats]);
    useEffect(() => { loadAnomalies(); }, [loadAnomalies]);
    useEffect(() => { loadPlomb(); }, [loadPlomb]);

    const goToNS = (tab) => {
        localStorage.setItem('nsActiveTab', tab);
        navigate('/ns');
    };

    const goToAnalysis = (device) => {
        localStorage.setItem('analysisSelectedDevice', JSON.stringify({
            name: device.deviceName, deviceType: device.deviceType,
            city: device.city, street: device.street,
            houseNumber: device.houseNumber, fullName: device.deviceName, status: 1,
        }));
        navigate('/analysis');
    };

    const goToHouse = (node) => {
        // ✅ Deep-link на конкретный дом (узел учёта) — паттерн как selectedDeviceFromMap
        localStorage.setItem('selectedHouseFromStats', JSON.stringify({
            city: node.city,
            street: node.street,
            houseNumber: node.houseNumber,
        }));
        navigate('/devices');
    };

    // Сводка по опломбированию
    const plombSummary = useMemo(() => {
        if (!plombList) return null;
        let expired = 0, warning = 0, ok = 0, none = 0;
        plombList.forEach(n => {
            const s = getPlombStatus(n).status;
            if (s === 'expired') expired++;
            else if (s === 'warning') warning++;
            else if (s === 'ok') ok++;
            else none++;
        });
        return { total: plombList.length, expired, warning, ok, none };
    }, [plombList]);

    const todayRaw = new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', weekday: 'long' });
    const today = todayRaw.charAt(0).toUpperCase() + todayRaw.slice(1);

    return (
        <div className="relative space-y-1">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Chakra+Petch:wght@500;600;700&display=swap');
                @keyframes stat-fade-up { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>

            {/* Фоновая подложка */}
            <div className="pointer-events-none absolute -inset-x-4 -top-4 h-72 overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(55%_100%_at_50%_0%,rgba(59,130,246,0.10),transparent_70%)]" />
                <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.04)_1px,transparent_1px)] bg-[size:34px_34px] [mask-image:linear-gradient(to_bottom,black,transparent)]" />
            </div>

            {/* ===== Шапка ===== */}
            <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4"
                style={{ animation: 'stat-fade-up .5s ease both' }}>
                <div>
                    <div className="flex items-center gap-2 text-blue-400/90 text-[11px] font-bold tracking-[0.22em] uppercase">
                        <Activity className="w-3.5 h-3.5" /> Оперативная сводка
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-white mt-1.5" style={DISPLAY_FONT}>Статистика</h2>
                    <p className="text-slate-400 text-sm mt-1 flex items-center gap-2 flex-wrap">
                        <CalendarClock className="w-4 h-4 text-slate-500" />
                        По состоянию на {today}
                        <span className="inline-flex items-center gap-1.5 text-emerald-400/90 text-xs font-medium">
                            <span className="relative flex h-1.5 w-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                            </span>
                            мониторинг активен
                        </span>
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {lastUpdated && (
                        <span className="text-slate-500 text-xs hidden sm:inline tabular-nums">
                            Обновлено: {lastUpdated.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </span>
                    )}
                    <button
                        onClick={() => { loadStats(); loadAnomalies(); loadPlomb(); }}
                        disabled={isLoading}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white rounded-lg text-sm font-medium transition flex items-center gap-2 shadow-lg shadow-blue-600/20"
                    >
                        <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} /> Обновить
                    </button>
                </div>
            </div>

            {/* ===== Ошибка ===== */}
            {!isLoading && error && (
                <div className="relative bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}</span>
                    <button onClick={loadStats} className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/40 rounded-lg text-xs font-medium transition whitespace-nowrap">Повторить</button>
                </div>
            )}

            {/* ===== Загрузка ===== */}
            {isLoading && (
                <div className="relative space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                        <div className="lg:col-span-2 bg-slate-800 border border-slate-700 rounded-xl h-40 animate-pulse" />
                        <div className="lg:col-span-3 space-y-4">
                            <div className="bg-slate-800 border border-slate-700 rounded-xl h-28 animate-pulse" />
                            <div className="bg-slate-800 border border-slate-700 rounded-xl h-28 animate-pulse" />
                        </div>
                    </div>
                    <div className="text-slate-400 text-sm flex items-center gap-2 justify-center py-4">
                        <Loader2 className="w-4 h-4 animate-spin text-blue-500" /> Собираем сводку...
                    </div>
                </div>
            )}

            {/* ===== Контент ===== */}
            {!isLoading && !error && stats && (
                <>
                    <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-4">
                        {/* ---------- Парк устройств ---------- */}
                        <section className="lg:col-span-2 bg-slate-800 border border-slate-700 rounded-xl overflow-hidden hover:border-slate-600 transition"
                            style={{ animation: 'stat-fade-up .55s ease both', animationDelay: '60ms' }}>
                            <div className="h-1 bg-gradient-to-r from-blue-600 via-cyan-500 to-transparent" />
                            <div className="p-4 sm:p-5">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-9 h-9 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
                                        <Factory className="w-4.5 h-4.5 text-blue-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-white font-semibold text-sm" style={DISPLAY_FONT}>Парк устройств</h3>
                                        <p className="text-slate-500 text-[11px]">Приборы учёта в системе</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-slate-900/50 border border-slate-700/60 rounded-lg px-3 py-3">
                                        <div className="flex items-center gap-1.5 text-slate-400 text-[10px] font-medium uppercase tracking-wider mb-1">
                                            <Gauge className="w-3 h-3 text-slate-500" /> Всего
                                        </div>
                                        <AnimatedNumber value={stats.totalDevices} className="text-2xl sm:text-3xl font-bold text-white leading-none" />
                                    </div>
                                    <div className="bg-slate-900/50 border border-slate-700/60 rounded-lg px-3 py-3">
                                        <div className="flex items-center gap-1.5 text-slate-400 text-[10px] font-medium uppercase tracking-wider mb-1">
                                            <Wrench className="w-3 h-3 text-emerald-500" /> Обслуживаемые
                                        </div>
                                        <AnimatedNumber value={stats.servicedDevices} className="text-2xl sm:text-3xl font-bold text-emerald-400 leading-none" />
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* ---------- Нештатные ситуации ---------- */}
                        <div className="lg:col-span-3 flex flex-col gap-4">
                            <div className="flex items-center gap-2 text-slate-400 text-[11px] font-bold tracking-[0.22em] uppercase"
                                style={{ animation: 'stat-fade-up .55s ease both', animationDelay: '120ms' }}>
                                <Siren className="w-3.5 h-3.5 text-red-400" /> Нештатные ситуации
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <section className="bg-slate-800 border border-slate-700 border-l-4 border-l-red-500 rounded-xl p-4 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/25 transition-all"
                                    style={{ animation: 'stat-fade-up .55s ease both', animationDelay: '160ms' }}>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-red-500/15 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                                            <WifiOff className="w-4.5 h-4.5 text-red-400" />
                                        </div>
                                        <div className="min-w-0">
                                            <AnimatedNumber value={stats.lostCount} className="text-2xl sm:text-3xl font-bold text-white leading-none" />
                                            <div className="text-slate-400 text-[11px] mt-0.5">без связи</div>
                                        </div>
                                    </div>
                                    {(stats.criticalLostCount ?? 0) > 0 && (
                                        <div className="mt-2.5 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-500/15 border border-red-500/40 text-red-400 text-[11px] font-bold">
                                            <span className="relative flex h-1.5 w-1.5">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
                                            </span>
                                            критично (7+ дн): {stats.criticalLostCount}
                                        </div>
                                    )}
                                    <button onClick={() => goToNS('lost')}
                                        className="mt-3 w-full px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 hover:text-red-200 rounded-lg text-xs font-semibold transition flex items-center justify-center gap-1.5 group/link">
                                        К вкладке «Устройства без связи»
                                        <ChevronRight className="w-3.5 h-3.5 group-hover/link:translate-x-0.5 transition-transform" />
                                    </button>
                                </section>

                                <section className="bg-slate-800 border border-slate-700 border-l-4 border-l-orange-500 rounded-xl p-4 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/25 transition-all"
                                    style={{ animation: 'stat-fade-up .55s ease both', animationDelay: '220ms' }}>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-orange-500/15 border border-orange-500/30 flex items-center justify-center flex-shrink-0">
                                            <AlertOctagon className="w-4.5 h-4.5 text-orange-400" />
                                        </div>
                                        <div className="min-w-0">
                                            <AnimatedNumber value={stats.devicesWithMultipleAlarms} className="text-2xl sm:text-3xl font-bold text-white leading-none" />
                                            <div className="text-slate-400 text-[11px] mt-0.5">с авариями (&gt;1)</div>
                                        </div>
                                    </div>
                                    <div className="text-slate-500 text-[11px] mt-2.5">
                                        {stats.devicesWithMultipleAlarms == null ? (<>откройте вкладку аварий для сканирования</>)
                                            : stats.alarmsScanDate ? (<>сканирование: {new Date(stats.alarmsScanDate).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</>)
                                                : (<>больше 1 активной аварии</>)}
                                    </div>
                                    <button onClick={() => goToNS('alarms')}
                                        className="mt-3 w-full px-3 py-2 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 text-orange-300 hover:text-orange-200 rounded-lg text-xs font-semibold transition flex items-center justify-center gap-1.5 group/link">
                                        К вкладке «Аварии и предупреждения»
                                        <ChevronRight className="w-3.5 h-3.5 group-hover/link:translate-x-0.5 transition-transform" />
                                    </button>
                                </section>
                            </div>
                        </div>
                    </div>

                    {/* ============================================================ */}
                    {/* ===== РЕЗКИЕ ИЗМЕНЕНИЯ ПОКАЗАНИЙ =========================== */}
                    {/* ============================================================ */}
                    <section className="relative bg-slate-800 border border-slate-700 rounded-xl overflow-hidden"
                        style={{ animation: 'stat-fade-up .55s ease both', animationDelay: '280ms' }}>
                        <div className="h-1 bg-gradient-to-r from-red-500 via-orange-500 to-transparent" />
                        <div className="p-4 sm:p-5">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-red-500/15 border border-red-500/30 flex items-center justify-center">
                                        <Zap className="w-4.5 h-4.5 text-red-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-white font-semibold text-sm" style={DISPLAY_FONT}>Резкие изменения показаний</h3>
                                        <p className="text-slate-500 text-[11px]">порог ≥ 30% за 10 дней • сравнительный анализ</p>
                                    </div>
                                </div>
                                <button onClick={loadAnomalies} disabled={anomaliesLoading}
                                    className="self-start sm:self-auto px-3 py-1.5 bg-slate-700/60 hover:bg-slate-700 border border-slate-600 text-slate-300 hover:text-white rounded-lg text-xs font-medium transition flex items-center gap-1.5 disabled:opacity-50">
                                    <RefreshCw className={`w-3 h-3 ${anomaliesLoading ? 'animate-spin' : ''}`} /> Обновить
                                </button>
                            </div>

                            {anomaliesLoading && (
                                <div className="flex flex-col items-center justify-center py-10 gap-3">
                                    <Loader2 className="w-6 h-6 animate-spin text-red-400" />
                                    <div className="text-slate-400 text-xs text-center">
                                        Сканирование суточных архивов всех устройств...<br />
                                        <span className="text-slate-500">может занять несколько минут при первом запуске</span>
                                    </div>
                                </div>
                            )}

                            {!anomaliesLoading && anomaliesError && (
                                <div className="bg-red-500/15 border border-red-500/40 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center justify-between gap-3">
                                    <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 flex-shrink-0" /> {anomaliesError}</span>
                                    <button onClick={loadAnomalies} className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/40 rounded-lg text-xs font-medium transition whitespace-nowrap">Повторить</button>
                                </div>
                            )}

                            {!anomaliesLoading && !anomaliesError && anomalies && anomalies.length === 0 && (
                                <div className="flex flex-col items-center justify-center py-10 text-center">
                                    <div className="w-12 h-12 rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center mb-3">
                                        <Inbox className="w-6 h-6 text-green-400" />
                                    </div>
                                    <p className="text-slate-300 text-sm font-medium">Резких изменений не обнаружено</p>
                                    <p className="text-slate-500 text-xs mt-1">Все показания в пределах нормы (отклонения &lt; 10%)</p>
                                </div>
                            )}

                            {!anomaliesLoading && !anomaliesError && anomalies && anomalies.length > 0 && (
                                <>
                                    <div className="flex items-center gap-2 mb-3 text-xs text-slate-400">
                                        <span className="font-semibold text-white tabular-nums">{anomalies.length}</span>
                                        {plural(anomalies.length, 'устройство', 'устройства', 'устройств')} с резкими изменениями
                                        {anomaliesFromCache && <span className="ml-auto text-slate-600 text-[10px] uppercase tracking-wider">кэш 30 мин</span>}
                                    </div>
                                    <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1 custom-scrollbar">
                                        {anomalies.map((device, idx) => (
                                            <div key={`${device.deviceName}-${idx}`}
                                                className="bg-slate-900/50 border border-slate-700/60 border-l-4 border-l-red-500/70 rounded-lg px-4 py-3 hover:border-slate-600 hover:bg-slate-900/80 transition group">
                                                <div className="flex items-start gap-3">
                                                    <div className="w-8 h-8 rounded-lg bg-red-500/10 border border-red-500/25 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                        {device.maxChangePercent > 0
                                                            ? <TrendingUp className="w-4 h-4 text-red-400" />
                                                            : <TrendingDown className="w-4 h-4 text-blue-400" />}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className="text-white text-sm font-semibold truncate">{device.deviceName}</span>
                                                            <span className="px-1.5 py-0.5 rounded bg-red-500/15 border border-red-500/30 text-red-400 text-[10px] font-bold tabular-nums whitespace-nowrap">
                                                                {device.anomalyCount} {plural(device.anomalyCount, 'аномалия', 'аномалии', 'аномалий')}
                                                            </span>
                                                            <span className="px-1.5 py-0.5 rounded bg-orange-500/15 border border-orange-500/30 text-orange-400 text-[10px] font-bold tabular-nums whitespace-nowrap">
                                                                макс. {device.maxChangePercent.toFixed(1).replace('.', ',')}%
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-1.5 text-slate-500 text-[11px] mt-1">
                                                            <MapPin className="w-3 h-3 flex-shrink-0" />
                                                            <span className="truncate">{formatAddress(device)}</span>
                                                            <span className="text-slate-600">•</span>
                                                            <span className="text-slate-500 whitespace-nowrap">{device.deviceType}</span>
                                                        </div>
                                                        <div className="flex flex-wrap gap-1 mt-2">
                                                            {device.dates.slice(0, 5).map((date, di) => (
                                                                <span key={di} className="px-1.5 py-0.5 rounded bg-slate-700/60 border border-slate-600/50 text-slate-300 text-[10px] tabular-nums">{date}</span>
                                                            ))}
                                                            {device.dates.length > 5 && <span className="px-1.5 py-0.5 text-slate-500 text-[10px]">+{device.dates.length - 5}</span>}
                                                        </div>
                                                        {device.topParams?.length > 0 && (
                                                            <div className="flex flex-wrap gap-1 mt-1.5">
                                                                {device.topParams.map((p, pi) => (
                                                                    <span key={pi} className="px-1.5 py-0.5 rounded bg-purple-500/10 border border-purple-500/25 text-purple-300 text-[10px] font-mono">{p}</span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button onClick={() => goToAnalysis(device)}
                                                        className="flex-shrink-0 px-2.5 py-2 rounded-lg bg-blue-600/15 hover:bg-blue-600/30 border border-blue-500/30 text-blue-400 hover:text-blue-300 transition opacity-70 group-hover:opacity-100"
                                                        title="Открыть в «Сравнительный анализ»">
                                                        <ExternalLink className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </section>

                    {/* ============================================================ */}
                    {/* ===== ГРАФИК ОПЛОМБИРОВАНИЯ ================================ */}
                    {/* ============================================================ */}
                    <section className="relative bg-slate-800 border border-slate-700 rounded-xl overflow-hidden"
                        style={{ animation: 'stat-fade-up .55s ease both', animationDelay: '340ms' }}>
                        <div className="h-1 bg-gradient-to-r from-violet-500 via-purple-500 to-transparent" />
                        <div className="p-4 sm:p-5">
                            {/* Заголовок */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
                                        <Lock className="w-4.5 h-4.5 text-violet-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-white font-semibold text-sm" style={DISPLAY_FONT}>График опломбирования</h3>
                                        <p className="text-slate-500 text-[11px]">узлы учёта • сортировка по дате истечения</p>
                                    </div>
                                </div>
                                <button onClick={() => { loadPlomb(); }} disabled={plombLoading}
                                    className="self-start sm:self-auto px-3 py-1.5 bg-slate-700/60 hover:bg-slate-700 border border-slate-600 text-slate-300 hover:text-white rounded-lg text-xs font-medium transition flex items-center gap-1.5 disabled:opacity-50">
                                    <RefreshCw className={`w-3 h-3 ${plombLoading ? 'animate-spin' : ''}`} /> Обновить
                                </button>
                            </div>

                            {/* Загрузка */}
                            {plombLoading && (
                                <div className="flex items-center justify-center py-10 gap-3">
                                    <Loader2 className="w-5 h-5 animate-spin text-violet-400" />
                                    <span className="text-slate-400 text-xs">Загрузка узлов учёта...</span>
                                </div>
                            )}

                            {/* Ошибка */}
                            {!plombLoading && plombError && (
                                <div className="bg-red-500/15 border border-red-500/40 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center justify-between gap-3">
                                    <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 flex-shrink-0" /> {plombError}</span>
                                    <button onClick={loadPlomb} className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/40 rounded-lg text-xs font-medium transition whitespace-nowrap">Повторить</button>
                                </div>
                            )}

                            {/* Пусто */}
                            {!plombLoading && !plombError && plombList && plombList.length === 0 && (
                                <div className="flex flex-col items-center justify-center py-10 text-center">
                                    <div className="w-12 h-12 rounded-full bg-slate-600/20 border border-slate-600/40 flex items-center justify-center mb-3">
                                        <Lock className="w-6 h-6 text-slate-500" />
                                    </div>
                                    <p className="text-slate-300 text-sm font-medium">Узлы учёта не найдены</p>
                                    <p className="text-slate-500 text-xs mt-1">Добавьте узлы учёта на странице «Объект учета»</p>
                                </div>
                            )}

                            {/* Список */}
                            {!plombLoading && !plombError && plombList && plombList.length > 0 && (
                                <>
                                    {/* Сводка */}
                                    {plombSummary && (
                                        <div className="flex flex-wrap items-center gap-3 mb-4">
                                            <span className="text-xs text-slate-400">
                                                Всего: <span className="text-white font-bold tabular-nums">{plombSummary.total}</span>
                                            </span>
                                            {plombSummary.expired > 0 && (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-500/15 border border-red-500/40 text-red-400 text-[11px] font-bold">
                                                    <ShieldX className="w-3 h-3" /> просрочено: {plombSummary.expired}
                                                </span>
                                            )}
                                            {plombSummary.warning > 0 && (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-yellow-500/15 border border-yellow-500/40 text-yellow-400 text-[11px] font-bold">
                                                    <ShieldAlert className="w-3 h-3" /> скоро: {plombSummary.warning}
                                                </span>
                                            )}
                                            {plombSummary.ok > 0 && (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-500/15 border border-green-500/40 text-green-400 text-[11px] font-bold">
                                                    <ShieldCheck className="w-3 h-3" /> действует: {plombSummary.ok}
                                                </span>
                                            )}
                                            {plombSummary.none > 0 && (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-slate-600/20 border border-slate-600/50 text-slate-400 text-[11px] font-bold">
                                                    <Lock className="w-3 h-3" /> не опломб.: {plombSummary.none}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* Таблица / список */}
                                    <div className="max-h-[480px] overflow-y-auto pr-1 custom-scrollbar space-y-1.5">
                                        {plombList.map((node) => {
                                            const plomb = getPlombStatus(node);
                                            const meta = PLOMB_META[plomb.status];
                                            const Icon = meta.icon;
                                            return (
                                                <div key={node.nodeId}
                                                    className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg border border-slate-700/60 border-l-4 ${meta.border} ${meta.bg} hover:bg-slate-700/30 transition group cursor-pointer`}
                                                    onClick={() => goToHouse(node)}
                                                    title="Открыть дом в «Объект учета»"
                                                >
                                                    {/* Иконка статуса */}
                                                    <div className="flex-shrink-0">
                                                        <Icon className={`w-4.5 h-4.5 ${meta.iconCls}`} />
                                                    </div>

                                                    {/* ✅ Адрес — на первом месте (крупнее), наименование узла — ниже (мельче) */}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-1.5 text-white text-sm font-medium">
                                                            <MapPin className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
                                                            <span className="truncate">{formatAddress(node)}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1.5 text-slate-400 text-[11px] mt-0.5">
                                                            <Package className="w-3 h-3 flex-shrink-0 text-slate-500" />
                                                            <span className="truncate">{node.nodeName}</span>
                                                            {node.description && (
                                                                <span className="text-slate-600 truncate hidden sm:inline">— {node.description}</span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Дата + остаток */}
                                                    <div className="flex-shrink-0 text-right">
                                                        {node.oplombirovanValidUntil ? (
                                                            <>
                                                                <div className="text-slate-300 text-xs tabular-nums font-medium">
                                                                    до {node.oplombirovanValidUntil}
                                                                </div>
                                                                <div className={`text-[11px] font-bold tabular-nums mt-0.5 ${plomb.status === 'expired' ? 'text-red-400'
                                                                        : plomb.status === 'warning' ? 'text-yellow-400'
                                                                            : 'text-green-400/80'
                                                                    }`}>
                                                                    {plomb.status === 'expired'
                                                                        ? `просрочено ${Math.abs(plomb.days)} ${plural(Math.abs(plomb.days), 'день', 'дня', 'дней')}`
                                                                        : `осталось ${plomb.days} ${plural(plomb.days, 'день', 'дня', 'дней')}`
                                                                    }
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <span className={`px-2 py-0.5 rounded text-[11px] font-medium border ${meta.badge}`}>
                                                                {plomb.label}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Бейдж статуса (мобильный) */}
                                                    <span className={`sm:hidden flex-shrink-0 px-2 py-0.5 rounded text-[10px] font-bold border ${meta.badge}`}>
                                                        {plomb.label}
                                                    </span>

                                                    {/* Стрелка */}
                                                    <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-slate-400 transition flex-shrink-0" />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                </>
            )}
        </div>
    );
};

export default StatisticsPage;